import { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Ban, Trash2, UserCheck } from "lucide-react";

const EMPTY = { code: "", name: "", active: true, approvers: [] };

export default function Departments() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [approverUsers, setApproverUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const isAdmin = can("admin");

  const load = useCallback(() => {
    api.get("/departments").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { load(); api.get("/requisitions/approvers").then((r) => setApproverUsers(r.data)).catch(() => {}); }, [load]);

  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) { toast.error(t("common.required")); return; }
    try {
      const payload = { ...form, approvers: form.approvers.filter((a) => a.user_id).map((a) => ({ user_id: a.user_id, position: a.position || "" })) };
      if (editId) await api.put(`/departments/${editId}`, payload);
      else await api.post("/departments", payload);
      toast.success(t("common.saved"));
      setOpen(false); setForm(EMPTY); setEditId(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openEdit = (r) => {
    setEditId(r.id);
    setForm({ code: r.code || "", name: r.name || "", active: r.active !== false,
      approvers: (r.approvers || []).map((a) => ({ user_id: a.user_id, position: a.position || "" })) });
    setOpen(true);
  };

  const deactivate = async (r) => {
    if (!window.confirm(`${t("pn.deactivate")}: ${r.name}?`)) return;
    try { await api.delete(`/departments/${r.id}`); toast.success(t("common.saved")); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const setApprover = (i, patch) => {
    const approvers = [...form.approvers];
    approvers[i] = { ...approvers[i], ...patch };
    setForm({ ...form, approvers });
  };

  return (
    <div data-testid="departments-page">
      <PageHeader title={t("dept.title")}>
        {isAdmin && (
          <Button size="sm" className="rounded-none text-xs font-bold" data-testid="dept-new-btn"
            onClick={() => { setEditId(null); setForm(EMPTY); setOpen(true); }}>
            <Plus size={12} className="mr-1" /> {t("dept.new")}
          </Button>
        )}
      </PageHeader>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(EMPTY); setEditId(null); } }}>
        <DialogContent className="rounded-none sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="uppercase font-black">{editId ? t("dept.edit") : t("dept.new")}</DialogTitle>
            <DialogDescription className="text-xs">{t("dept.approversHint")}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-[10px] font-bold uppercase">{t("dept.code")}</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                className="rounded-none h-9 text-xs font-mono" data-testid="dept-form-code" />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] font-bold uppercase">{t("dept.name")}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-none h-9 text-xs" data-testid="dept-form-name" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs font-bold">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="dept-form-active" />
            {t("pn.active")}
          </label>
          <div className="space-y-2 border-t pt-3" data-testid="dept-approvers-section">
            <Label className="text-[10px] font-bold uppercase tracking-widest flex items-center gap-1"><UserCheck size={12} /> {t("dept.approvers")}</Label>
            {form.approvers.map((a, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`dept-approver-row-${i}`}>
                <Select value={a.user_id || ""} onValueChange={(v) => setApprover(i, { user_id: v })}>
                  <SelectTrigger className="rounded-none h-8 text-xs flex-1" data-testid={`dept-approver-user-${i}`}><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{approverUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} — {u.email}</SelectItem>)}</SelectContent>
                </Select>
                <Input value={a.position} onChange={(e) => setApprover(i, { position: e.target.value })} placeholder={t("dept.position")}
                  className="rounded-none h-8 text-xs w-56" data-testid={`dept-approver-position-${i}`} />
                <button onClick={() => setForm({ ...form, approvers: form.approvers.filter((_, j) => j !== i) })}
                  className="text-muted-foreground hover:text-destructive" data-testid={`dept-approver-remove-${i}`}><Trash2 size={13} /></button>
              </div>
            ))}
            <Button variant="outline" size="sm" className="rounded-none text-xs" data-testid="dept-add-approver"
              onClick={() => setForm({ ...form, approvers: [...form.approvers, { user_id: "", position: "" }] })}>
              <Plus size={12} className="mr-1" /> {t("dept.addApprover")}
            </Button>
          </div>
          <div className="flex justify-end">
            <Button size="sm" className="rounded-none text-xs font-bold" onClick={save} data-testid="dept-form-save">{t("common.save")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {rows.length === 0 ? <Empty text={t("common.noData")} /> : (
        <div className="tbl-wrap">
          <table className="tbl" data-testid="dept-table">
            <thead><tr>
              <th>{t("dept.code")}</th><th>{t("dept.name")}</th><th>{t("dept.approvers")}</th><th>{t("pn.active")}</th><th></th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`dept-row-${r.id}`}>
                  <td className="font-bold font-mono">{r.code}</td>
                  <td>{r.name}</td>
                  <td className="text-[11px]" data-testid={`dept-approvers-${r.code}`}>
                    {(r.approvers || []).length === 0 ? <span className="text-muted-foreground">—</span>
                      : r.approvers.map((a) => <div key={a.user_id}><b>{a.name}</b>{a.position && <span className="text-muted-foreground"> · {a.position}</span>}</div>)}
                  </td>
                  <td>
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 ${r.active ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                      {r.active ? t("pn.active") : t("pn.inactive")}
                    </span>
                  </td>
                  <td>
                    {isAdmin && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="h-7 px-2 rounded-none" onClick={() => openEdit(r)} data-testid={`dept-edit-${r.id}`}><Pencil size={12} /></Button>
                        {r.active && (
                          <Button size="sm" variant="ghost" className="h-7 px-2 rounded-none text-destructive" onClick={() => deactivate(r)} data-testid={`dept-deactivate-${r.id}`}><Ban size={12} /></Button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
